import {AriaRole, createElement, forwardRef, JSX, ReactNode} from "react";
import classNames from "classnames";
import { Filter } from "@/types/app.type";

export interface BaseProps {
  id?: string;
  /**
   * The text content of the element.
   * @default 'Base'
   * @type string
   * @required
   */
  children: string | number | ReactNode | ReactNode;
  /**
   * The component type.
   * @type 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'label' | 'p' | 'q'
   * @required
   */
  component: keyof Pick<
    React.JSX.IntrinsicElements,
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "span"
    | "label"
    | "p"
    | "time"
    | "a"
    | "q"
  >;
  /**
   * The component custom className.
   * @type string
   */
  className?: string;
  /**
   * Unique id to test the element.
   * @type string
   */
  "data-test-id"?: string;
  /**
   * The text color
   * @type string
   */
  color?: string;
  /**
   * The text custom styles
   * @type React.CSSProperties
   */
  style?: React.CSSProperties;
  /**
   * The text  role
   * @type | 'button' | 'link' | 'label' | 'heading' | 'article' | 'caption' | 'listitem' | 'menuitem' | 'menuitemcheckbox' | 'menuitemradio' | 'option' | 'presentation' | 'none'
   * @required
   */
  role: Filter<
    AriaRole,
    | "button"
    | "link"
    | "inputlabel"
    | "heading"
    | "article"
    | "caption"
    | "listitem"
    | "menuitem"
    | "menuitemcheckbox"
    | "menuitemradio"
    | "option"
    | "presentation"
    | "none"
  >;
  /**
   * The label for if associated with an input.
   * @type string
   * @optional
   */
  htmlFor?: string;
  /**
   * The title of the element.
   * @type string
   * @optional
   */
  title?: string;
  /**
   * The alignment of the heading.
   * @type "left" | "center" | "right"
   */
  hAlignContent?: "left" | "center" | "right";
  /**
   * Enable animation triggered whe the element is visible.
   * @type boolean
   * @optional
   */
  animated?: boolean;
  /**
   * The animation type triggered whe the element is visible. Default is fadeIn
   * @type string
   * @optional
   */
  animation?: string;
}

const tags: Record<string, keyof JSX.IntrinsicElements> = {
  button: "button",
  link: "a",
  inputlabel: "label",
  heading: "h2",
  article: "article",
  caption: "h3",
  span: "span"
};

// const BaseTypography = forwardRef<HTMLElement, BaseProps>((props, ref) =>
//   createElement(tags.hasOwnProperty(props.role) && tags[props.role] ? tags[props.role] : (
//     ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'label', 'p', 'time', 'a', 'q'].includes(props?.component)
//         ? props?.component
//         : 'div'
//     ), 
//     {
//       ref: ref,
//       role: props.role,
//       id: props.id,
//       ...(props.role === "heading" ? {"aria-level": 2} : {}),
//       "data-test-id": props["data-test-id"],
//       className: classNames(props.color, props.className),
//       dangerouslySetInnerHTML: { __html: props.children },
//       style: {
//         textAlign: props.hAlignContent,
//         color: props.color,
//         ...props.style
//       },
//       htmlFor: props.htmlFor,
//       title: props.title,
//       //"data-aos": "fade-in",
//       "data-aos": props.animated ? (props.animation || 'fade-in') : ''
//     })
// );

// const BaseTypography = forwardRef<HTMLElement, BaseProps>((props, ref) =>
//   createElement(props.component, {
//     ref: ref,
//     role: props.role,
//     id: props.id,
//     "data-test-id": props["data-test-id"],
//     className: classNames(props.color, props.className),
//     dangerouslySetInnerHTML: { __html: props.children },
//     style: {
//       textAlign: props.hAlignContent,
//       color: props.color,
//       ...props.style
//     },
//     htmlFor: props.htmlFor,
//     title: props.title,
//     //"data-aos": "fade-in",
//     "data-aos": props.animated ? (props.animation || 'fade-in') : ''
//   })
// );

const BaseTypography = forwardRef<HTMLElement, BaseProps>((props, ref) => {
  const {
    role,
    component,
    hAlignContent,
    color,
    className,
    animated,
    animation,
    style,
    id,
    htmlFor,
    title,
    children,
    ...rest
  } = props;

  const Tag = tags.hasOwnProperty(role || '') && tags[role!]
    ? tags[role!]
    : ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'label', 'p', 'time', 'a', 'q'].includes(component || '')
      ? (props?.component as keyof JSX.IntrinsicElements)
      : 'div';

  return createElement(
    Tag,
    {
      ref,
      role,
      id,
      ...(role === 'heading' ? { 'aria-level': 2 } : {}),
      'data-test-id': props['data-test-id'],
      className: classNames(color, className),
      style: {
        textAlign: hAlignContent,
        color,
        ...style,
      },
      htmlFor,
      title,
      "aria-label": title,
      ...((()=>{
        let obj: {[key: string]: string | number} = {};
        if(animated) {
          obj['data-aos'] = animation || 'fade-in';
          // if(props['data-aos-easing']) {
          //     obj['data-aos-easing'] = props['data-aos-easing'];
          // }
          // if(props['data-aos-anchor-placemen']) {
          //     obj['data-aos-anchor-placemen'] = props['data-aos-anchor-placemen'];
          // }
          // if(props['data-aos-offset']) {
          //     obj['data-aos-offset'] = props['data-aos-offset'];
          // }
          // if(props['data-aos-once']) {
          //     obj['data-aos-once'] = props['data-aos-once'];
          // }
          // if(props['data-aos-duration']) {
          //     obj['data-aos-duration'] = props['data-aos-duration'];
          // }
          // if(props['data-aos-delay']) {
          //     obj['data-aos-delay'] = props['data-aos-delay'];
          // }
          // if(props['data-aos-anchor-placement']) {
          //     obj['data-aos-anchor-placement'] = props['data-aos-anchor-placement'];
          // }
        }
        return obj;
      })()),
      ...rest,
    },
    children // ✅ Safe JSX children
  );
});

BaseTypography.displayName = 'BaseTypography';
export default BaseTypography;